import {
  AnimatedFormError,
  Button,
  cn,
  Flex,
  Input,
  Loading,
  Select,
  SelectOption,
  Textarea,
  Typography,
} from "djuno-design";
import {
  Control,
  useController,
  useFieldArray,
  FieldErrors,
  UseFormRegister,
  Controller,
} from "react-hook-form";
import { ReactComponent as PlusIcon } from "./../../../assets/icons/plus.svg";
import { ReactComponent as ArchiveIcon } from "./../../../assets/icons/archive-box.svg";
import { CreateNotebookFormType } from "../../../pages/ai-notebooks/AiNotebookCreatePage";
import { RadioGroup } from "@headlessui/react";
import {
  AiNotebookEditor,
  AiNotebookFreamwork,
  AiRegionData,
  AiResourceFlavor,
  AiResourceUseCaseType,
} from "../../../types/ai";
import { InstanceSshKey, OvhRegion } from "../../../types/instance";
import { useEffect, useMemo, useState } from "react";
import {
  continents,
  ContinentType,
  datacenterDetails,
  getCountryFlag,
} from "../../../utils/countries";
import { Tabs, tabsClasses } from "@mui/material";
import { a11yProps, MuiTabItem, MuiTabPanel } from "../../general/MuiTab";
import { useAppSelector } from "../../../hooks";
import {
  selectAiResourceFlavors,
  selectAiResourceFlavorsLoading,
} from "../../../store/ai/notebooks/notebooksSlice";
import { NumberStepper } from "../../general/NumberStepper";
import { humanizeSize } from "../../../utils/file";
import {
  selectInstancesSshKeys,
  selectInstancesSshKeysLoading,
} from "../../../store/instances/instancesSSHkeysSlice";

export const resourceUseCases: Array<{
  value: AiResourceUseCaseType;
  title: string;
  description: string;
}> = [
  {
    value: "cpu",
    title: "AI Notebooks Standard (CPU resources)",
    description:
      "A lower price, offering standard performance. Suitable for learning, testing or workloads that only require CPU.",
  },
  {
    value: "gpu",
    title: "AI Notebooks Advanced (GPU resources)",
    description:
      "Powered by up to four GPUs and dozens of CPUs. Delivers higher performance for deep learning and intensive workloads.",
  },
];

export const AiNotebookConfigInput: React.FC<{
  control: Control<CreateNotebookFormType, any>;
  register: UseFormRegister<CreateNotebookFormType>;
  errors: FieldErrors<CreateNotebookFormType>;
}> = ({ control, register, errors }) => {
  const {
    field: { value: notebookName, onChange: onChangeNotebookName },
  } = useController({ name: "notebookName", control });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "labels",
  });

  return (
    <Flex direction="col" className="gap-5">
      <Input
        label="Notebook Name"
        value={notebookName}
        onChange={onChangeNotebookName}
        error={errors.notebookName?.message as string | undefined}
      />

      <Flex direction="col" className="w-full mt-3 mb-1">
        <Typography.Text size="sm">Labels</Typography.Text>
        <Typography.Text size="sm" uiType="secondary" className="mb-3">
          You can add labels to your notebook.
        </Typography.Text>
        {fields.map((_, index) => (
          <div key={index} className="grid gap-3 grid-cols-12 pb-2 w-full">
            <div className="col-span-4">
              <Input
                label=""
                {...register(`labels.${index}.key` as const)}
                error={
                  errors.labels &&
                  errors.labels[index] &&
                  errors.labels[index]?.key &&
                  errors.labels[index]?.key?.message
                }
                placeholder="Key"
              />
            </div>
            <div className="col-span-8">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    label=""
                    {...register(`labels.${index}.value` as const)}
                    error={
                      errors.labels &&
                      errors.labels[index] &&
                      errors.labels[index]?.value &&
                      errors.labels[index]?.value?.message
                    }
                    placeholder="Value"
                    className="flex-1"
                  />
                </div>
                <Button
                  uiType="icon"
                  className="!px-2 group"
                  onClick={(e) => {
                    e.preventDefault();
                    remove(index);
                  }}
                >
                  <ArchiveIcon className="w-5 text-slate-700 dark:text-slate-300 group-hover:text-red-500 group-hover:dark:text-red-400" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        <AnimatedFormError error={errors.labels?.root?.message} />
        <AnimatedFormError error={errors.labels?.message} />
        <Flex
          items="center"
          justify="start"
          className="mt-1 gap-2 w-full flex-wrap"
        >
          <Flex
            items="center"
            justify="start"
            className="mt-1 gap-2 w-full flex-wrap"
          >
            <Button
              uiType="light"
              onClick={(e) => {
                if (fields.length >= 10) {
                  return;
                }
                e.preventDefault();
                append({
                  key: "",
                  value: "",
                });
              }}
              disabled={fields.length >= 10}
            >
              <PlusIcon className="w-4" />
              Add a label
            </Button>
            <Typography.Text size="sm" uiType="secondary">
              {fields.length}/10 labels
            </Typography.Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const AiNotebookEditorInput: React.FC<{
  control: Control<CreateNotebookFormType, any>;
  editors: AiNotebookEditor[];
  currentValue?: string;
  errorMessage?: string;
  loading?: boolean;
}> = ({ control, editors, errorMessage, loading }) => {
  if (loading)
    return (
      <Flex items="center" justify="center" className="min-h-[200px]">
        <Loading borderSize={2} />
      </Flex>
    );
  return (
    <Flex direction="col" className="gap-3">
      <Controller
        name="selectedEditor"
        control={control}
        render={({ field: { value, onChange } }) => {
          return (
            <div className="w-full">
              <RadioGroup value={value || null} onChange={onChange}>
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5 mt-3">
                  {editors.map((editor, i) => {
                    return (
                      <RadioGroup.Option key={i} value={editor.id}>
                        {({ checked, disabled }) => {
                          return (
                            <div
                              className={cn(
                                "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                                {
                                  "border-primary-400 dark:border-primary-400":
                                    checked,
                                  "!cursor-not-allowed opacity-60": disabled,
                                }
                              )}
                            >
                              <div className="flex w-full justify-between items-center gap-2">
                                <Typography.Text className="!text-sm">
                                  {editor.name}
                                </Typography.Text>
                                {editor.logoUrl && (
                                  <img
                                    src={editor.logoUrl}
                                    alt={editor.name}
                                    className="w-8 h-8 aspect-square flex-shrink-0"
                                  />
                                )}
                              </div>
                              <div className="flex items-center border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                <Typography.Text className="!text-xs font-medium">
                                  {editor.description}
                                </Typography.Text>
                              </div>
                            </div>
                          );
                        }}
                      </RadioGroup.Option>
                    );
                  })}
                </div>
              </RadioGroup>
            </div>
          );
        }}
      />
      <AnimatedFormError error={errorMessage} />
    </Flex>
  );
};

export const AiNotebookFreamworkInput: React.FC<{
  control: Control<CreateNotebookFormType, any>;
  freamworks: AiNotebookFreamwork[];
  errorMessage?: string;
  loading?: boolean;
}> = ({ control, freamworks, loading, errorMessage }) => {
  const {
    field: { value: version, onChange: onChangeVersion },
  } = useController({ name: "selectedFreamworkVersion", control });

  if (loading)
    return (
      <Flex items="center" justify="center" className="min-h-[200px]">
        <Loading borderSize={2} />
      </Flex>
    );
  return (
    <Flex direction="col" className="gap-3">
      <Controller
        name="selectedFreamwork"
        control={control}
        render={({ field: { value, onChange } }) => {
          return (
            <div className="w-full">
              <RadioGroup
                value={value || null}
                onChange={(freamworkId: string) => {
                  const _selectedFreamwork = freamworks.find(
                    (f) => f.id === freamworkId
                  );
                  if (_selectedFreamwork) {
                    onChange(_selectedFreamwork.id);
                    onChangeVersion(_selectedFreamwork.versions[0]);
                  }
                }}
              >
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5 mt-3">
                  {freamworks.map((freamwork, i) => {
                    return (
                      <RadioGroup.Option key={i} value={freamwork.id}>
                        {({ checked, disabled }) => {
                          return (
                            <div
                              className={cn(
                                "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                                {
                                  "border-primary-400 dark:border-primary-400":
                                    checked,
                                  "!cursor-not-allowed opacity-60": disabled,
                                }
                              )}
                            >
                              <div className="flex w-full justify-between items-center gap-2 h-9">
                                <Typography.Text className="!text-sm">
                                  {freamwork.name}
                                </Typography.Text>
                                {freamwork.logoUrl && (
                                  <img
                                    src={freamwork.logoUrl}
                                    alt={freamwork.name}
                                    className="h-7 flex-shrink-0"
                                  />
                                )}
                              </div>
                              <div className="flex items-center border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                {freamwork.versions.length > 1 ? (
                                  <Select
                                    className="!w-full"
                                    optionsClassName="!max-h-[250px]"
                                    options={[
                                      ...freamwork.versions.map((v) => ({
                                        label: v,
                                        value: v,
                                      })),
                                    ]}
                                    value={
                                      value === freamwork.id
                                        ? version
                                        : freamwork.versions[0]
                                    }
                                    onChange={onChangeVersion}
                                  />
                                ) : (
                                  <Flex items="center" className="h-9">
                                    <Typography.Text
                                      size="sm"
                                      uiType="secondary"
                                    >
                                      {freamwork.versions[0]}
                                    </Typography.Text>
                                  </Flex>
                                )}
                              </div>
                              <div className="flex items-center border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                <Typography.Text className="!text-xs font-medium">
                                  {freamwork.description}
                                </Typography.Text>
                              </div>
                            </div>
                          );
                        }}
                      </RadioGroup.Option>
                    );
                  })}
                </div>
              </RadioGroup>
            </div>
          );
        }}
      />
      <AnimatedFormError error={errorMessage} />
    </Flex>
  );
};

export const AiNotebookRegionInput: React.FC<{
  regions: OvhRegion[];
  regionsData: AiRegionData[];
  control: Control<CreateNotebookFormType, any>;
  errorMessage: string | undefined;
  loading?: boolean;
  disabled?: boolean;
}> = ({ regions, regionsData, control, errorMessage, loading, disabled }) => {
  const [tabValue, setTabValue] = useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const activeRegions = useMemo(() => {
    return regions.filter((r) => r.status === "UP");
  }, [regions]);

  const continentsData: Array<
    ContinentType & {
      regions: OvhRegion[];
    }
  > = useMemo(() => {
    const filterRegions = (regions: typeof activeRegions, include: boolean) => {
      const regionsDataName = regionsData.map((r) => r.id);
      let filtered = regions.filter((r) =>
        include
          ? regionsDataName.includes(r.name)
          : !regionsDataName.includes(r.name)
      );

      return filtered;
    };

    const allContinents = continents.map((continent) => {
      const { datacenterLocations } = continent;

      const regionsInContinent = datacenterLocations
        ? activeRegions.filter((r) =>
            datacenterLocations.includes(r.datacenterLocation)
          )
        : activeRegions;

      const regions = filterRegions(regionsInContinent, true);

      return {
        ...continent,
        regions,
      };
    });
    return allContinents.filter((c) => c.regions.length > 0);
  }, [activeRegions, regionsData]);

  if (loading) {
    return (
      <Flex justify="center" items="center" className="w-full !min-h-[100px]">
        <Loading borderSize={2} />
      </Flex>
    );
  }
  return (
    <Flex direction="col" className="gap-4">
      {/* regions */}
      <Flex direction="col">
        <Typography.Text className="!text-sm font-semibold">
          Select a region
        </Typography.Text>
        <Controller
          name="selectedRegionDataId"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <>
                <Tabs
                  value={tabValue}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                  variant="scrollable"
                  sx={{
                    [`& .${tabsClasses.scrollButtons}`]: {
                      "&.Mui-disabled": { opacity: 0.3 },
                    },
                  }}
                >
                  {continentsData.map((continent, index) => (
                    <MuiTabItem
                      key={index}
                      label={continent.continentTitle}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>

                <RadioGroup
                  value={value || null}
                  disabled={disabled}
                  onChange={onChange}
                >
                  {continentsData.map((continent, index) => (
                    <MuiTabPanel value={tabValue} index={index} key={index}>
                      <div className="flex flex-col w-full mt-3">
                        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5">
                          {continent.regions.map((region, i) => (
                            <RadioGroup.Option key={i} value={region.name}>
                              {({ checked, disabled }) => {
                                const datacenter = datacenterDetails.find(
                                  (dc) =>
                                    dc.location === region.datacenterLocation
                                );
                                const regionImage = getCountryFlag(
                                  region.countryCode,
                                  "iso2"
                                );
                                return (
                                  <div
                                    className={cn(
                                      "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                                      {
                                        "border-primary-400 dark:border-primary-400":
                                          checked,
                                        "!cursor-not-allowed": disabled,
                                      }
                                    )}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <Typography.Text className="!text-sm">
                                        {datacenter
                                          ? datacenter.title
                                          : region.name}
                                      </Typography.Text>
                                      {regionImage !== "" && (
                                        <img
                                          alt={region.name}
                                          src={regionImage}
                                          className="w-4 h-4"
                                        />
                                      )}
                                    </div>
                                    <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                      <Typography.Text
                                        uiType="secondary"
                                        className="!text-xs"
                                      >
                                        {region.name.toUpperCase()}
                                      </Typography.Text>
                                    </div>
                                  </div>
                                );
                              }}
                            </RadioGroup.Option>
                          ))}
                        </div>
                        <AnimatedFormError error={errorMessage} />
                      </div>
                    </MuiTabPanel>
                  ))}
                </RadioGroup>
              </>
            );
          }}
        />
      </Flex>
    </Flex>
  );
};

export const AiNotebookFlavorInput: React.FC<{
  control: Control<CreateNotebookFormType, any>;
  errorMessage: string | undefined;
  loading?: boolean;
  disabled?: boolean;
}> = ({ control, errorMessage, loading, disabled }) => {
  const [selectedUseCase, setSelectedUseCate] =
    useState<AiResourceUseCaseType>("cpu");

  const flavors = useAppSelector(selectAiResourceFlavors);
  const flavorsLoading = useAppSelector(selectAiResourceFlavorsLoading);

  const [filteredFlavors, setFilteredFlavors] = useState<AiResourceFlavor[]>(
    []
  );

  const {
    field: { value: selectedResourceId, onChange: onChangeSelectedResourceId },
  } = useController({ name: "selectedResourceId", control });

  const {
    field: {
      value: selectedResourceUnit,
      onChange: onChangeSelectedResourceUnit,
    },
  } = useController({ name: "selectedResourceUnit", control });

  // find selected flavor from its id
  const selectedFlavor = useMemo(() => {
    return filteredFlavors.find((f) => f.id === selectedResourceId);
  }, [filteredFlavors, selectedResourceId]);

  // filter showable flavors based on selected use-case
  useEffect(() => {
    if (flavorsLoading) {
      return;
    }
    const _filteredFlavors = flavors.filter(
      (flavor) => flavor.type === selectedUseCase
    );
    setFilteredFlavors(_filteredFlavors);
  }, [flavors, flavorsLoading, selectedUseCase]);

  // find default flavor from filtered flavors
  useEffect(() => {
    if (filteredFlavors && filteredFlavors.length) {
      const defultFlavor = filteredFlavors.find((f) => f.default);
      if (defultFlavor) {
        onChangeSelectedResourceId(defultFlavor.id);
      }
    }
  }, [filteredFlavors, onChangeSelectedResourceId]);

  // check max unit of selected flavor and update unit value
  useEffect(() => {
    if (selectedFlavor) {
      const selectedFlavorMaxUnit = selectedFlavor.max;
      if (selectedFlavorMaxUnit < selectedResourceUnit) {
        onChangeSelectedResourceUnit(selectedFlavorMaxUnit);
      }
    }
  }, [onChangeSelectedResourceUnit, selectedFlavor, selectedResourceUnit]);

  // reset unit after changing use-case
  useEffect(() => {
    onChangeSelectedResourceUnit(1);
  }, [onChangeSelectedResourceUnit, selectedUseCase]);

  if (loading || flavorsLoading) {
    return (
      <Flex justify="center" items="center" className="w-full !min-h-[100px]">
        <Loading borderSize={2} />
      </Flex>
    );
  }

  return (
    <Flex direction="col" className="gap-4">
      {/* Use case */}
      <Flex direction="col">
        <Typography.Text className="!text-sm font-semibold">
          Use case
        </Typography.Text>
        <RadioGroup
          value={selectedUseCase || null}
          disabled={disabled}
          onChange={(v) => setSelectedUseCate(v || undefined)}
        >
          <div className="flex flex-col w-full mt-3">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5">
              {resourceUseCases.map((useCase, i) => (
                <RadioGroup.Option key={i} value={useCase.value}>
                  {({ checked, disabled }) => {
                    return (
                      <div
                        className={cn(
                          "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                          {
                            "border-primary-400 dark:border-primary-400":
                              checked,
                            "!cursor-not-allowed": disabled,
                          }
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Typography.Text className="!text-sm">
                            {useCase.title}
                          </Typography.Text>
                        </div>
                        <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                          <Typography.Text
                            uiType="secondary"
                            className="!text-xs"
                          >
                            {useCase.description}
                          </Typography.Text>
                        </div>
                      </div>
                    );
                  }}
                </RadioGroup.Option>
              ))}
            </div>
          </div>
        </RadioGroup>
      </Flex>

      {/* flavors */}
      <Flex direction="col">
        <Typography.Text className="!text-sm font-semibold">
          Available resources{" "}
          <Typography.Text>
            Show {selectedUseCase.toUpperCase()} instances
          </Typography.Text>
        </Typography.Text>
        <Controller
          name="selectedResourceId"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <>
                <RadioGroup
                  value={value || null}
                  disabled={disabled}
                  onChange={onChange}
                >
                  <div className="grid gap-4 grid-cols-1 md:grid-cols-2 my-5">
                    {filteredFlavors.map((flavor, i) => (
                      <RadioGroup.Option key={i} value={flavor.id}>
                        {({ checked, disabled }) => {
                          return (
                            <div
                              className={cn(
                                "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                                {
                                  "border-primary-400 dark:border-primary-400":
                                    checked,
                                  "!cursor-not-allowed": disabled,
                                }
                              )}
                            >
                              <Flex direction="col" className="gap-1 mb-2">
                                <Typography.Text className="!text-sm !font-semibold">
                                  {flavor.id.toUpperCase()}
                                </Typography.Text>

                                {flavor.type === "cpu" && (
                                  <Typography.Text className="!text-xs">
                                    Model: {flavor.description}
                                  </Typography.Text>
                                )}

                                {flavor.type === "gpu" &&
                                  flavor.gpuInformation && (
                                    <Flex
                                      direction="col"
                                      className="gap-1 w-full"
                                    >
                                      <Typography.Text className="!text-xs">
                                        {selectedResourceUnit} x{" "}
                                        {flavor.description}
                                      </Typography.Text>
                                      <Typography.Text className="!text-xs">
                                        {selectedResourceUnit} x{" "}
                                        {humanizeSize(
                                          flavor.gpuInformation.gpuMemory
                                        ).join("B")}{" "}
                                        RAM
                                      </Typography.Text>
                                    </Flex>
                                  )}
                              </Flex>
                              <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                <Typography.Text
                                  className="!text-xs mt-1"
                                  uiType="secondary"
                                >
                                  {`CPU: ${
                                    selectedResourceUnit *
                                    flavor.resourcesPerUnit.cpu
                                  } vCore`}
                                </Typography.Text>
                                <Typography.Text
                                  className="!text-xs mt-1"
                                  uiType="secondary"
                                >
                                  {`RAM: ${humanizeSize(
                                    selectedResourceUnit *
                                      flavor.resourcesPerUnit.memory
                                  ).join("B")}`}
                                </Typography.Text>
                                <Typography.Text
                                  className="!text-xs mt-1"
                                  uiType="secondary"
                                >
                                  {`Temporary local storage: ${humanizeSize(
                                    selectedResourceUnit *
                                      flavor.resourcesPerUnit.ephemeralStorage
                                  ).join("B")} SSD`}
                                </Typography.Text>
                                <Typography.Text
                                  className="!text-xs mt-1"
                                  uiType="secondary"
                                >
                                  {`Public network: ${humanizeSize(
                                    selectedResourceUnit *
                                      flavor.resourcesPerUnit.publicNetwork,
                                    { binaryBaseValue: 1000, fractionDigits: 2 }
                                  ).join("B/s")}`}
                                </Typography.Text>
                              </div>
                              <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                <Typography.Text
                                  uiType="secondary"
                                  className="!text-xs"
                                >
                                  Resource Price
                                </Typography.Text>
                              </div>
                            </div>
                          );
                        }}
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </>
            );
          }}
        />
        <NumberStepper
          min={1}
          max={selectedFlavor?.max || 1}
          value={selectedResourceUnit}
          onChange={onChangeSelectedResourceUnit}
        />
        <AnimatedFormError error={errorMessage} />
      </Flex>
    </Flex>
  );
};

export const AiNotebookAttachRepoInput: React.FC<{
  control: Control<CreateNotebookFormType, any>;
  register: UseFormRegister<CreateNotebookFormType>;
  errors: FieldErrors<CreateNotebookFormType>;
}> = ({ control, register, errors }) => {
  const permissions = [
    { label: "Read-only", value: "RO" },
    { label: "Read & Write", value: "RW" },
    { label: "Read & Write & Delete", value: "RWD" },
  ];
  const {
    fields: gitRepoFields,
    append: appendGitRepo,
    remove: removeGitRepo,
  } = useFieldArray({
    control,
    name: "gitRepos",
  });

  return (
    <Flex direction="col" className="gap-5">
      <Flex direction="col" className="w-full mt-3 mb-1">
        <Flex direction="col" className="w-full gap-4">
          {gitRepoFields.map((_, index) => (
            <div
              key={index}
              className="grid gap-3 grid-cols-12 w-full rounded-lg border-2 dark:bg-dark-3 dark:border-gray-400/10 bg-white"
            >
              <div className="col-span-11 flex flex-col gap-3 p-4">
                <Input
                  label="Git repository URL"
                  {...register(`gitRepos.${index}.url` as const)}
                  error={
                    errors.gitRepos && errors.gitRepos[index]?.url?.message
                  }
                  placeholder=""
                />
                <Input
                  label="Mount directory"
                  {...register(`gitRepos.${index}.mountPath` as const)}
                  error={
                    errors.gitRepos &&
                    errors.gitRepos[index]?.mountPath?.message
                  }
                  placeholder="/workspace/demo"
                  className="flex-1"
                />
                <Controller
                  control={control}
                  name={`gitRepos.${index}.permission` as const}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        key={index}
                        label="Permission"
                        tooltip={{
                          content:
                            "In ‘Read Only’ mode, no sync is launched at the end of the job. This way, the job finishes quicker.",
                          className: "text-xs",
                        }}
                        options={permissions}
                        value={value}
                        onChange={onChange}
                        error={
                          errors.gitRepos &&
                          errors.gitRepos[index]?.permission?.message
                        }
                      />
                    );
                  }}
                />
              </div>
              <div className="col-span-1 flex justify-center items-center border-l-2  dark:bg-dark-3 dark:border-gray-400/10">
                <Button
                  uiType="icon"
                  className="!px-2 group"
                  onClick={(e) => {
                    e.preventDefault();
                    removeGitRepo(index);
                  }}
                >
                  <ArchiveIcon className="w-5 text-slate-700 dark:text-slate-300 group-hover:text-red-500 group-hover:dark:text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </Flex>
        <AnimatedFormError error={errors.gitRepos?.root?.message} />
        <AnimatedFormError error={errors.gitRepos?.message} />
        <Flex
          items="center"
          justify="start"
          className="mt-1 gap-2 w-full flex-wrap"
        >
          <Flex
            items="center"
            justify="start"
            className="mt-1 gap-2 w-full flex-wrap"
          >
            <Button
              uiType="light"
              onClick={(e) => {
                if (
                  gitRepoFields.length >= 10 ||
                  (errors.gitRepos &&
                    errors.gitRepos.length !== undefined &&
                    errors.gitRepos.length > 0)
                ) {
                  return;
                }
                e.preventDefault();
                appendGitRepo({
                  url: "",
                  mountPath: "",
                  permission: "RO",
                });
              }}
              disabled={
                gitRepoFields.length >= 10 ||
                (errors.gitRepos &&
                  errors.gitRepos.length !== undefined &&
                  errors.gitRepos.length > 0)
              }
            >
              <PlusIcon className="w-4" />
              Attach a public Git repository
            </Button>
            <Typography.Text size="sm" uiType="secondary">
              {gitRepoFields.length}/10 volumes
            </Typography.Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const AiNotebookAttachSshInput: React.FC<{
  control: Control<CreateNotebookFormType, any>;
  register: UseFormRegister<CreateNotebookFormType>;
  errors: FieldErrors<CreateNotebookFormType>;
}> = ({ control, register, errors }) => {
  const sshKeys = useAppSelector(selectInstancesSshKeys);
  const sshKeysLoading = useAppSelector(selectInstancesSshKeysLoading);

  const NewSskKeyName = "+ Add New";

  const existKeysOptions: Array<SelectOption<InstanceSshKey>> = useMemo(() => {
    return [
      { value: "0", label: NewSskKeyName, extraData: undefined },
      ...sshKeys.map((k) => ({
        value: k.id,
        label: k.name,
        extraData: k,
      })),
    ];
  }, [sshKeys]);

  const {
    fields: sshFields,
    append: appendSsh,
    remove: removeSsh,
    update: updateSsh,
  } = useFieldArray({
    control,
    name: "sshKeys",
  });

  return (
    <Flex direction="col" className="gap-5">
      <Flex direction="col" className="w-full mt-3 mb-1">
        <Flex direction="col" className="w-full gap-4">
          {sshFields.map((sf, index) => (
            <div
              key={index}
              className="grid gap-3 grid-cols-12 w-full rounded-lg border-2 dark:bg-dark-3 dark:border-gray-400/10 bg-white"
            >
              <div className="col-span-11 flex flex-col gap-3 p-4">
                <Controller
                  control={control}
                  name={`sshKeys.${index}.name` as const}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        key={index}
                        label="Name"
                        options={existKeysOptions}
                        value={value}
                        onChange={(value: string | undefined) => {
                          onChange(value);
                          let content = "";
                          if (value !== "0") {
                            content =
                              existKeysOptions.find((k) => k.value === value)
                                ?.extraData?.publicKey || "";
                          }
                          updateSsh(index, {
                            name: value || "0",
                            content,
                            disabled: value !== "0",
                          });
                        }}
                        error={
                          errors.sshKeys && errors.sshKeys[index]?.name?.message
                        }
                      />
                    );
                  }}
                />
                <Textarea
                  label="Content"
                  {...register(`sshKeys.${index}.content` as const)}
                  disabled={sf.disabled}
                  error={
                    errors.sshKeys && errors.sshKeys[index]?.content?.message
                  }
                  placeholder=""
                />
              </div>
              <div className="col-span-1 flex justify-center items-center border-l-2  dark:bg-dark-3 dark:border-gray-400/10">
                <Button
                  uiType="icon"
                  className="!px-2 group"
                  onClick={(e) => {
                    e.preventDefault();
                    removeSsh(index);
                  }}
                >
                  <ArchiveIcon className="w-5 text-slate-700 dark:text-slate-300 group-hover:text-red-500 group-hover:dark:text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </Flex>
        <AnimatedFormError error={errors.sshKeys?.root?.message} />
        <AnimatedFormError error={errors.sshKeys?.message} />
        <Flex
          items="center"
          justify="start"
          className="mt-1 gap-2 w-full flex-wrap"
        >
          <Flex
            items="center"
            justify="start"
            className="mt-1 gap-2 w-full flex-wrap"
          >
            <Button
              uiType="light"
              onClick={(e) => {
                if (
                  sshFields.length >= 10 ||
                  (errors.sshKeys &&
                    errors.sshKeys.length !== undefined &&
                    errors.sshKeys.length > 0)
                ) {
                  return;
                }
                e.preventDefault();
                appendSsh({
                  name: "",
                  content: "",
                  disabled: false,
                });
              }}
              disabled={
                sshFields.length >= 10 ||
                (errors.sshKeys &&
                  errors.sshKeys.length !== undefined &&
                  errors.sshKeys.length > 0)
              }
            >
              <PlusIcon className="w-4" />
              Attach a SSH
            </Button>
            <Typography.Text size="sm" uiType="secondary">
              {sshFields.length}/10
            </Typography.Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
